import { GroupId } from '../config/systemConfig'
import { LEVELS } from '../config/levels'

export type Phase = 'B' | 'M' | 'E'

/**
 * Resolve the school year phase based on the current date.
 *
 * Logic:
 * - Aug 1 (Month 7) – Oct 31 (Month 9) = 'B' (Begin)
 * - Nov 1 (Month 10) – Mar 31 (Month 2) = 'M' (Midden)
 * - Apr 1 (Month 3) – Jul 31 (Month 6) = 'E' (Eind)
 */
export function resolvePhaseByDate(date: Date = new Date()): Phase {
  const month = date.getMonth() // 0 = Jan, 11 = Dec

  // Aug (7) to Oct (9) -> B
  if (month >= 7 && month <= 9) return 'B'

  // Nov (10) to Dec (11) OR Jan (0) to Mar (2) -> M
  if (month >= 10 || month <= 2) return 'M'

  // Apr (3) to Jul (6) -> E
  return 'E'
}

/**
 * Get the automatic starting level ID for a group based on the current date.
 * Returns the ID of the first level (non-plus) for the resolved phase.
 */
export function getAutoStartingLevel(
  group: GroupId,
  date: Date = new Date(),
): number {
  const phase = resolvePhaseByDate(date)

  // Find the level matching group + phase + !isPlus
  const level = LEVELS.find(
    (l) => l.group === group && l.period === phase && !l.isPlus,
  )

  if (level) return level.id

  // Fallback: return the first level of the group
  const firstLevel = LEVELS.find((l) => l.group === group)
  return firstLevel ? firstLevel.id : 101 // Default to B6 (101) if all else fails
}

/**
 * Get a human-readable label for the current auto-detected phase
 */
export function getAutoPhaseLabel(date: Date = new Date()): string {
  const phase = resolvePhaseByDate(date)
  switch (phase) {
    case 'B':
      return 'Begin schooljaar'
    case 'M':
      return 'Midden schooljaar'
    case 'E':
      return 'Eind schooljaar'
  }
}
